/**
 * Language utilities and translations for the application
 */

// Available languages
export const LANGUAGES = {
  EN: 'en',
  MY: 'my' // Bahasa Malaysia
};

// Default language
export const DEFAULT_LANGUAGE = LANGUAGES.EN;

// All translations for the application
export const translations = {
  // App general translations
  app: {
    title: {
      [LANGUAGES.EN]: 'RumahKira',
      [LANGUAGES.MY]: 'RumahKira'
    },
    subtitle: {
      [LANGUAGES.EN]: 'Build with Confidence, Budget with RumahKira',
      [LANGUAGES.MY]: 'Build with Confidence, Budget with RumahKira'
    }
  },
  
  // Navigation translations
  nav: {
    home: {
      [LANGUAGES.EN]: 'Home',
      [LANGUAGES.MY]: 'Laman Utama'
    },
    constructionPhases: {
      [LANGUAGES.EN]: 'Construction Phases',
      [LANGUAGES.MY]: 'Fasa Pembinaan'
    },
    budgetManagement: {
      [LANGUAGES.EN]: 'Budget',
      [LANGUAGES.MY]: 'Bajet'
    },
    phaseSettings: {
      [LANGUAGES.EN]: 'Phase Settings',
      [LANGUAGES.MY]: 'Tetapan Fasa'
    },
    materialCalculator: {
      [LANGUAGES.EN]: 'Calculator',
      [LANGUAGES.MY]: 'Kalkulator'
    },
    reportsExport: {
      [LANGUAGES.EN]: 'Reports',
      [LANGUAGES.MY]: 'Laporan'
    },
    settings: {
      [LANGUAGES.EN]: 'Settings',
      [LANGUAGES.MY]: 'Tetapan'
    }
  },
  
  // Homepage translations
  home: {
    welcome: {
      [LANGUAGES.EN]: 'Welcome back!',
      [LANGUAGES.MY]: 'Selamat Kembali!'
    },
    projectOverview: {
      [LANGUAGES.EN]: 'Your Project Overview',
      [LANGUAGES.MY]: 'Ringkasan Projek Anda'
    },
    complete: {
      [LANGUAGES.EN]: 'Complete',
      [LANGUAGES.MY]: 'Selesai'
    },
    totalBudget: {
      [LANGUAGES.EN]: 'Total Budget',
      [LANGUAGES.MY]: 'Jumlah Bajet'
    },
    totalSpent: {
      [LANGUAGES.EN]: 'Total Spent',
      [LANGUAGES.MY]: 'Jumlah Dibelanjakan'
    },
    remaining: {
      [LANGUAGES.EN]: 'Remaining',
      [LANGUAGES.MY]: 'Baki'
    },
    remainingBudget: {
      [LANGUAGES.EN]: 'Remaining Budget',
      [LANGUAGES.MY]: 'Baki Bajet'
    },
    activeTasks: {
      [LANGUAGES.EN]: 'Active Tasks',
      [LANGUAGES.MY]: 'Tugasan Aktif'
    },
    projectProgress: {
      [LANGUAGES.EN]: 'Project Progress',
      [LANGUAGES.MY]: 'Kemajuan Projek'
    },
    overview: {
      [LANGUAGES.EN]: 'Track your construction progress and expenses in one place',
      [LANGUAGES.MY]: 'Jejaki kemajuan dan perbelanjaan pembinaan anda dalam satu tempat'
    },
    quickAccess: {
      [LANGUAGES.EN]: 'Quick Access',
      [LANGUAGES.MY]: 'Akses Pantas'
    },
    openFeature: {
      [LANGUAGES.EN]: 'Open',
      [LANGUAGES.MY]: 'Buka'
    },
    tipTitle: {
      [LANGUAGES.EN]: 'Construction Tip',
      [LANGUAGES.MY]: 'Tip Pembinaan'
    },
    tipContent: {
      [LANGUAGES.EN]: 'Remember to regularly update your expenses and monitor your budget to prevent cost overruns. Use the Material Calculator to accurately estimate required quantities.',
      [LANGUAGES.MY]: 'Ingat untuk mengemaskini perbelanjaan anda dan memantau bajet anda secara berkala untuk mengelakkan kos berlebihan. Gunakan Kalkulator Bahan untuk menganggarkan kuantiti yang diperlukan dengan tepat.'
    },
    footer: {
      [LANGUAGES.EN]: 'RumahKira - House Construction Budgeting App © 2023',
      [LANGUAGES.MY]: 'RumahKira - Aplikasi Belanjawan Pembinaan Rumah © 2023'
    },
    features: {
      constructionPhases: {
        [LANGUAGES.EN]: 'Track your construction progress through organized phases',
        [LANGUAGES.MY]: 'Jejaki kemajuan pembinaan anda melalui fasa yang teratur'
      },
      budgetManagement: {
        [LANGUAGES.EN]: 'Manage your budget and expenses for each phase',
        [LANGUAGES.MY]: 'Urus bajet dan perbelanjaan anda untuk setiap fasa'
      },
      materialCalculator: {
        [LANGUAGES.EN]: 'Calculate material quantities and costs',
        [LANGUAGES.MY]: 'Kira kuantiti dan kos bahan'
      },
      reportsExport: {
        [LANGUAGES.EN]: 'Generate detailed reports and export data',
        [LANGUAGES.MY]: 'Jana laporan terperinci dan eksport data'
      }
    }
  },
  
  // Common buttons and actions
  common: {
    add: {
      [LANGUAGES.EN]: 'Add',
      [LANGUAGES.MY]: 'Tambah'
    },
    edit: {
      [LANGUAGES.EN]: 'Edit',
      [LANGUAGES.MY]: 'Sunting'
    },
    delete: {
      [LANGUAGES.EN]: 'Delete',
      [LANGUAGES.MY]: 'Padam'
    },
    save: {
      [LANGUAGES.EN]: 'Save',
      [LANGUAGES.MY]: 'Simpan'
    },
    cancel: {
      [LANGUAGES.EN]: 'Cancel',
      [LANGUAGES.MY]: 'Batal'
    },
    close: {
      [LANGUAGES.EN]: 'Close',
      [LANGUAGES.MY]: 'Tutup'
    },
    confirm: {
      [LANGUAGES.EN]: 'Confirm',
      [LANGUAGES.MY]: 'Sahkan'
    },
    back: {
      [LANGUAGES.EN]: 'Back',
      [LANGUAGES.MY]: 'Kembali'
    },
    next: {
      [LANGUAGES.EN]: 'Next',
      [LANGUAGES.MY]: 'Seterusnya'
    },
    search: {
      [LANGUAGES.EN]: 'Search',
      [LANGUAGES.MY]: 'Cari'
    },
    filter: {
      [LANGUAGES.EN]: 'Filter',
      [LANGUAGES.MY]: 'Tapis'
    },
    reset: {
      [LANGUAGES.EN]: 'Reset',
      [LANGUAGES.MY]: 'Set Semula'
    },
    selectLanguage: {
      [LANGUAGES.EN]: 'Select Language',
      [LANGUAGES.MY]: 'Pilih Bahasa'
    },
    update: {
      [LANGUAGES.EN]: 'Update',
      [LANGUAGES.MY]: 'Kemaskini'
    },
    exportToExcel: {
      [LANGUAGES.EN]: 'Export to Excel',
      [LANGUAGES.MY]: 'Eksport ke Excel'
    },
    hide: {
      [LANGUAGES.EN]: 'Hide',
      [LANGUAGES.MY]: 'Sembunyi'
    },
    show: {
      [LANGUAGES.EN]: 'Show',
      [LANGUAGES.MY]: 'Tunjuk'
    },
    languageSelector: {
      title: {
        [LANGUAGES.EN]: 'Change Language',
        [LANGUAGES.MY]: 'Tukar Bahasa'
      },
      english: {
        [LANGUAGES.EN]: 'English',
        [LANGUAGES.MY]: 'Bahasa Inggeris'
      },
      malay: {
        [LANGUAGES.EN]: 'Bahasa Malaysia',
        [LANGUAGES.MY]: 'Bahasa Malaysia'
      }
    },
    units: {
      [LANGUAGES.EN]: 'units',
      [LANGUAGES.MY]: 'unit'
    },
    pieces: {
      [LANGUAGES.EN]: 'pieces',
      [LANGUAGES.MY]: 'keping'
    },
    panels: {
      [LANGUAGES.EN]: 'panels',
      [LANGUAGES.MY]: 'panel'
    },
    sheets: {
      [LANGUAGES.EN]: 'sheets',
      [LANGUAGES.MY]: 'kepingan'
    },
    bags: {
      [LANGUAGES.EN]: 'bags',
      [LANGUAGES.MY]: 'beg'
    },
    packages: {
      [LANGUAGES.EN]: 'packages',
      [LANGUAGES.MY]: 'paket'
    }
  },
  
  // Common form fields
  form: {
    required: {
      [LANGUAGES.EN]: 'Required',
      [LANGUAGES.MY]: 'Diperlukan'
    },
    optional: {
      [LANGUAGES.EN]: 'Optional',
      [LANGUAGES.MY]: 'Pilihan'
    },
    enterValue: {
      [LANGUAGES.EN]: 'Please enter a value',
      [LANGUAGES.MY]: 'Sila masukkan nilai'
    },
    invalidValue: {
      [LANGUAGES.EN]: 'Invalid value',
      [LANGUAGES.MY]: 'Nilai tidak sah'
    },
    notes: {
      [LANGUAGES.EN]: 'Notes',
      [LANGUAGES.MY]: 'Nota'
    },
    name: {
      [LANGUAGES.EN]: 'Name',
      [LANGUAGES.MY]: 'Nama'
    },
    description: {
      [LANGUAGES.EN]: 'Description',
      [LANGUAGES.MY]: 'Penerangan'
    },
    nameDescription: {
      [LANGUAGES.EN]: 'Name/Description',
      [LANGUAGES.MY]: 'Nama/Penerangan'
    }
  },
  
  // Settings component translations
  settings: {
    darkMode: {
      [LANGUAGES.EN]: 'Dark Mode',
      [LANGUAGES.MY]: 'Mod Gelap'
    },
    language: {
      [LANGUAGES.EN]: 'Language',
      [LANGUAGES.MY]: 'Bahasa'
    },
    english: {
      [LANGUAGES.EN]: 'English',
      [LANGUAGES.MY]: 'Bahasa Inggeris'
    },
    malay: {
      [LANGUAGES.EN]: 'Bahasa Malaysia',
      [LANGUAGES.MY]: 'Bahasa Malaysia'
    },
    switchTheme: {
      [LANGUAGES.EN]: 'Switch between light and dark theme',
      [LANGUAGES.MY]: 'Tukar antara tema cerah dan gelap'
    },
    appVersion: {
      [LANGUAGES.EN]: 'App Version',
      [LANGUAGES.MY]: 'Versi Aplikasi'
    },
    currentVersion: {
      [LANGUAGES.EN]: 'Current version',
      [LANGUAGES.MY]: 'Versi semasa'
    },
    about: {
      [LANGUAGES.EN]: 'About',
      [LANGUAGES.MY]: 'Tentang'
    },
    viewAbout: {
      [LANGUAGES.EN]: 'View About',
      [LANGUAGES.MY]: 'Lihat Tentang'
    },
    viewInfoAboutApp: {
      [LANGUAGES.EN]: 'View information about this app',
      [LANGUAGES.MY]: 'Lihat maklumat tentang aplikasi ini'
    },
    backToSettings: {
      [LANGUAGES.EN]: 'Back to Settings',
      [LANGUAGES.MY]: 'Kembali ke Tetapan'
    },
    aboutThisApp: {
      [LANGUAGES.EN]: 'About This App',
      [LANGUAGES.MY]: 'Tentang Aplikasi Ini'
    },
    supportFaq: {
      [LANGUAGES.EN]: 'Support FAQ',
      [LANGUAGES.MY]: 'Soalan Lazim Sokongan'
    },
    supportThisApp: {
      [LANGUAGES.EN]: 'Support This App',
      [LANGUAGES.MY]: 'Sokong Aplikasi Ini'
    },
    buyMeCoffee: {
      [LANGUAGES.EN]: 'Buy Me a Coffee',
      [LANGUAGES.MY]: 'Belikan Saya Kopi'
    },
    supportDeveloper: {
      [LANGUAGES.EN]: 'Support the Developer',
      [LANGUAGES.MY]: 'Sokong Pembangun'
    },
    supportAppText: {
      [LANGUAGES.EN]: 'If you find this app helpful for managing your construction project, please consider supporting its continued development. Your support helps keep this app updated and free to use.',
      [LANGUAGES.MY]: 'Jika anda mendapati aplikasi ini berguna untuk menguruskan projek pembinaan anda, sila pertimbangkan untuk menyokong pembangunannya yang berterusan. Sokongan anda membantu menjadikan aplikasi ini dikemas kini dan bebas untuk digunakan.'
    },
    keyFeatures: {
      [LANGUAGES.EN]: 'Key Features',
      [LANGUAGES.MY]: 'Ciri-ciri Utama'
    },
    phaseTracking: {
      [LANGUAGES.EN]: 'Phase-Based Tracking',
      [LANGUAGES.MY]: 'Penjejakan Berasaskan Fasa'
    },
    phaseTrackingDesc: {
      [LANGUAGES.EN]: 'Organize and track expenses according to construction phases, from foundation to finishing works.',
      [LANGUAGES.MY]: 'Susun dan jejaki perbelanjaan mengikut fasa pembinaan, dari asas hingga kerja kemasan.'
    },
    budgetManagement: {
      [LANGUAGES.EN]: 'Budget Management',
      [LANGUAGES.MY]: 'Pengurusan Belanjawan'
    },
    budgetManagementDesc: {
      [LANGUAGES.EN]: 'Set budgets for each phase, monitor spending, and visualize budget utilization with progress indicators.',
      [LANGUAGES.MY]: 'Tetapkan belanjawan untuk setiap fasa, pantau perbelanjaan, dan visualkan penggunaan belanjawan dengan penunjuk kemajuan.'
    },
    expenseTracking: {
      [LANGUAGES.EN]: 'Detailed Expense Tracking',
      [LANGUAGES.MY]: 'Penjejakan Perbelanjaan Terperinci'
    },
    expenseTrackingDesc: {
      [LANGUAGES.EN]: 'Track quantity, unit prices, transportation costs, and keep notes for each material or service purchased.',
      [LANGUAGES.MY]: 'Jejaki kuantiti, harga unit, kos pengangkutan, dan simpan nota untuk setiap bahan atau perkhidmatan yang dibeli.'
    },
    appDescription: {
      part1: {
        [LANGUAGES.EN]: 'I developed this application after struggling to properly manage and calculate the budget for my new home construction project. Traditional spreadsheets weren\'t providing the organization and phase-based tracking I needed, so I created this solution for an easier way to manage construction expenses.',
        [LANGUAGES.MY]: 'Saya membangunkan aplikasi ini selepas menghadapi kesukaran untuk menguruskan dan mengira belanjawan dengan betul untuk projek pembinaan rumah baru saya. Hamparan tradisional tidak memberikan organisasi dan penjejakan berasaskan fasa yang saya perlukan, jadi saya mencipta penyelesaian ini untuk cara yang lebih mudah untuk menguruskan perbelanjaan pembinaan.'
      },
      part2: {
        [LANGUAGES.EN]: 'As a homeowner embarking on a construction project, I found it challenging to keep track of expenses across different construction phases, monitor budgets, and maintain an accurate overview of where my money was going. This app is the result of addressing those challenges.',
        [LANGUAGES.MY]: 'Sebagai pemilik rumah yang memulakan projek pembinaan, saya mendapati sukar untuk menjejaki perbelanjaan merentasi fasa pembinaan yang berbeza, memantau belanjawan, dan mengekalkan gambaran keseluruhan yang tepat tentang ke mana wang saya pergi. Aplikasi ini adalah hasil daripada menangani cabaran tersebut.'
      },
      part3: {
        [LANGUAGES.EN]: 'RumahKira allows you to organize expenses by construction phases, track material quantities and costs, account for transportation fees, and generate detailed Excel reports that can be edited later for further analysis or sharing with contractors and family members.',
        [LANGUAGES.MY]: 'RumahKira membolehkan anda menyusun perbelanjaan mengikut fasa pembinaan, menjejaki kuantiti dan kos bahan, mengambil kira yuran pengangkutan, dan menjana laporan Excel terperinci yang boleh disunting kemudian untuk analisis lanjut atau perkongsian dengan kontraktor dan ahli keluarga.'
      }
    }
  },
  
  // Craftsmen Wages component translations
  craftsmen: {
    title: {
      [LANGUAGES.EN]: 'Craftsmen Wages',
      [LANGUAGES.MY]: 'Upah Tukang'
    },
    addCraftsman: {
      [LANGUAGES.EN]: 'Add Craftsman',
      [LANGUAGES.MY]: 'Tambah Tukang'
    },
    editCraftsman: {
      [LANGUAGES.EN]: 'Edit Craftsman',
      [LANGUAGES.MY]: 'Sunting Tukang'
    },
    craftType: {
      [LANGUAGES.EN]: 'Craft Type',
      [LANGUAGES.MY]: 'Jenis Kemahiran'
    },
    nameDescription: {
      [LANGUAGES.EN]: 'Name/Description',
      [LANGUAGES.MY]: 'Nama/Penerangan'
    },
    rateAmount: {
      [LANGUAGES.EN]: 'Rate/Amount',
      [LANGUAGES.MY]: 'Kadar/Amaun'
    },
    actions: {
      [LANGUAGES.EN]: 'Actions',
      [LANGUAGES.MY]: 'Tindakan'
    },
    perSqftRate: {
      [LANGUAGES.EN]: 'Per Square Foot Rate',
      [LANGUAGES.MY]: 'Kadar Per Kaki Persegi'
    },
    lumpsumAmount: {
      [LANGUAGES.EN]: 'Lumpsum Amount',
      [LANGUAGES.MY]: 'Amaun Sekaligus'
    },
    wagePerSqft: {
      [LANGUAGES.EN]: 'Wage Per Square Foot',
      [LANGUAGES.MY]: 'Upah Per Kaki Persegi'
    },
    paymentType: {
      [LANGUAGES.EN]: 'Payment Type',
      [LANGUAGES.MY]: 'Jenis Pembayaran'
    },
    constructionPhases: {
      [LANGUAGES.EN]: 'Construction Phases',
      [LANGUAGES.MY]: 'Fasa Pembinaan'
    },
    totalWage: {
      [LANGUAGES.EN]: 'Total Wage',
      [LANGUAGES.MY]: 'Jumlah Upah'
    },
    deleteConfirm: {
      [LANGUAGES.EN]: 'Delete Craftsman?',
      [LANGUAGES.MY]: 'Padam Tukang?'
    },
    deleteConfirmMessage: {
      [LANGUAGES.EN]: 'Are you sure you want to delete this craftsman? This action cannot be undone.',
      [LANGUAGES.MY]: 'Adakah anda pasti mahu memadam tukang ini? Tindakan ini tidak boleh dibatalkan.'
    },
    addFirstCraftsman: {
      [LANGUAGES.EN]: 'Add Your First Craftsman',
      [LANGUAGES.MY]: 'Tambah Tukang Pertama Anda'
    },
    houseSize: {
      [LANGUAGES.EN]: 'House Size',
      [LANGUAGES.MY]: 'Saiz Rumah'
    },
    enterHouseSize: {
      [LANGUAGES.EN]: 'Enter the total square footage of your house',
      [LANGUAGES.MY]: 'Masukkan jumlah kaki persegi rumah anda'
    },
    totalWages: {
      [LANGUAGES.EN]: 'Total Wages Estimation',
      [LANGUAGES.MY]: 'Anggaran Jumlah Upah'
    },
    perSqftWages: {
      [LANGUAGES.EN]: 'Per sq ft wages',
      [LANGUAGES.MY]: 'Upah per kaki persegi'
    },
    lumpsumWages: {
      [LANGUAGES.EN]: 'Lumpsum wages',
      [LANGUAGES.MY]: 'Upah sekaligus'
    },
    basedOn: {
      [LANGUAGES.EN]: 'Based on',
      [LANGUAGES.MY]: 'Berdasarkan'
    },
    craftsmenAt: {
      [LANGUAGES.EN]: 'craftsmen at',
      [LANGUAGES.MY]: 'tukang pada'
    },
    sqft: {
      [LANGUAGES.EN]: 'sq ft',
      [LANGUAGES.MY]: 'kaki persegi'
    },
    exampleRates: {
      [LANGUAGES.EN]: 'Example Rates',
      [LANGUAGES.MY]: 'Kadar Contoh'
    },
    commonWageRates: {
      [LANGUAGES.EN]: 'Common Craftsmen Wage Rates (Per Square Foot)',
      [LANGUAGES.MY]: 'Kadar Upah Tukang Biasa (Per Kaki Persegi)'
    },
    ratesNote: {
      [LANGUAGES.EN]: 'Note: These are example rates and may vary based on region, experience, and project complexity.',
      [LANGUAGES.MY]: 'Nota: Ini adalah kadar contoh dan mungkin berbeza-beza berdasarkan rantau, pengalaman, dan kerumitan projek.'
    },
    noCraftsmen: {
      [LANGUAGES.EN]: 'No Craftsmen Added Yet',
      [LANGUAGES.MY]: 'Belum Ada Tukang Ditambah'
    },
    addCraftsmenNote: {
      [LANGUAGES.EN]: 'Add craftsmen and their wages per square foot to calculate labor costs.',
      [LANGUAGES.MY]: 'Tambah tukang dan upah mereka per kaki persegi untuk mengira kos buruh.'
    },
    craftTypeHelper: {
      [LANGUAGES.EN]: 'Carpenter, Plumber, Electrician...',
      [LANGUAGES.MY]: 'Tukang Kayu, Tukang Paip, Juruelektrik...'
    },
    nameDescHelper: {
      [LANGUAGES.EN]: 'Optional name or description',
      [LANGUAGES.MY]: 'Nama atau penerangan pilihan'
    },
    phases: {
      [LANGUAGES.EN]: 'Phases',
      [LANGUAGES.MY]: 'Fasa'
    },
    allPhases: {
      [LANGUAGES.EN]: 'All phases',
      [LANGUAGES.MY]: 'Semua fasa'
    },
    phase: {
      [LANGUAGES.EN]: 'Phase',
      [LANGUAGES.MY]: 'Fasa'
    },
    total: {
      [LANGUAGES.EN]: 'TOTAL',
      [LANGUAGES.MY]: 'JUMLAH'
    },
    addNewCraftsman: {
      [LANGUAGES.EN]: 'Add New Craftsman',
      [LANGUAGES.MY]: 'Tambah Tukang Baru'
    },
    validationMessages: {
      enterCraftType: {
        [LANGUAGES.EN]: 'Please enter the craft type',
        [LANGUAGES.MY]: 'Sila masukkan jenis kemahiran'
      },
      enterLumpsum: {
        [LANGUAGES.EN]: 'Please enter a valid lumpsum amount',
        [LANGUAGES.MY]: 'Sila masukkan amaun sekaligus yang sah'
      },
      enterWagePerSqft: {
        [LANGUAGES.EN]: 'Please enter a valid wage per square foot',
        [LANGUAGES.MY]: 'Sila masukkan upah yang sah per kaki persegi'
      },
      addOneCraftsman: {
        [LANGUAGES.EN]: 'Please add at least one craftsman before exporting report.',
        [LANGUAGES.MY]: 'Sila tambah sekurang-kurangnya satu tukang sebelum mengeksport laporan.'
      },
      exportError: {
        [LANGUAGES.EN]: 'Error exporting report. Please try again.',
        [LANGUAGES.MY]: 'Ralat mengeksport laporan. Sila cuba lagi.'
      },
      exportSuccess: {
        [LANGUAGES.EN]: 'Report exported successfully as',
        [LANGUAGES.MY]: 'Laporan berjaya dieksport sebagai'
      }
    },
    exportReport: {
      title: {
        [LANGUAGES.EN]: 'CRAFTSMEN WAGES REPORT',
        [LANGUAGES.MY]: 'LAPORAN UPAH TUKANG'
      },
      subtitle: {
        [LANGUAGES.EN]: 'Complete breakdown of labor costs for your construction project',
        [LANGUAGES.MY]: 'Perincian lengkap kos buruh untuk projek pembinaan anda'
      },
      reportDate: {
        [LANGUAGES.EN]: 'Report Date',
        [LANGUAGES.MY]: 'Tarikh Laporan'
      },
      houseSize: {
        [LANGUAGES.EN]: 'House Size',
        [LANGUAGES.MY]: 'Saiz Rumah'
      },
      financialSummary: {
        [LANGUAGES.EN]: 'FINANCIAL SUMMARY',
        [LANGUAGES.MY]: 'RINGKASAN KEWANGAN'
      },
      description: {
        [LANGUAGES.EN]: 'Description',
        [LANGUAGES.MY]: 'Penerangan'
      },
      amount: {
        [LANGUAGES.EN]: 'Amount',
        [LANGUAGES.MY]: 'Amaun'
      },
      percentage: {
        [LANGUAGES.EN]: 'Percentage',
        [LANGUAGES.MY]: 'Peratusan'
      },
      perSqftWages: {
        [LANGUAGES.EN]: 'Per Square Foot Wages',
        [LANGUAGES.MY]: 'Upah Per Kaki Persegi'
      },
      lumpsumWages: {
        [LANGUAGES.EN]: 'Lumpsum Wages',
        [LANGUAGES.MY]: 'Upah Sekaligus'
      },
      grandTotal: {
        [LANGUAGES.EN]: 'GRAND TOTAL',
        [LANGUAGES.MY]: 'JUMLAH BESAR'
      },
      visualBreakdown: {
        [LANGUAGES.EN]: 'VISUAL COST BREAKDOWN',
        [LANGUAGES.MY]: 'PECAHAN KOS VISUAL'
      },
      perSqft: {
        [LANGUAGES.EN]: 'Per Square Foot',
        [LANGUAGES.MY]: 'Per Kaki Persegi'
      },
      lumpsum: {
        [LANGUAGES.EN]: 'Lumpsum',
        [LANGUAGES.MY]: 'Sekaligus'
      },
      projectStats: {
        [LANGUAGES.EN]: 'PROJECT STATISTICS',
        [LANGUAGES.MY]: 'STATISTIK PROJEK'
      },
      totalCraftsmen: {
        [LANGUAGES.EN]: 'Total Craftsmen',
        [LANGUAGES.MY]: 'Jumlah Tukang'
      },
      avgWagePerCraftsman: {
        [LANGUAGES.EN]: 'Average Wage per Craftsman',
        [LANGUAGES.MY]: 'Purata Upah setiap Tukang'
      },
      avgCostPerSqft: {
        [LANGUAGES.EN]: 'Average Cost per sq ft',
        [LANGUAGES.MY]: 'Purata Kos setiap kaki persegi'
      },
      mostCommonType: {
        [LANGUAGES.EN]: 'Most Common Craft Type',
        [LANGUAGES.MY]: 'Jenis Kemahiran Paling Biasa'
      },
      craftsmen: {
        [LANGUAGES.EN]: 'craftsmen',
        [LANGUAGES.MY]: 'tukang'
      },
      details: {
        [LANGUAGES.EN]: 'CRAFTSMEN DETAILS',
        [LANGUAGES.MY]: 'BUTIRAN TUKANG'
      },
      notes: {
        [LANGUAGES.EN]: 'Notes',
        [LANGUAGES.MY]: 'Nota'
      },
      reportNotes: {
        [LANGUAGES.EN]: 'REPORT NOTES',
        [LANGUAGES.MY]: 'NOTA LAPORAN'
      },
      note1: {
        [LANGUAGES.EN]: 'This report provides a comprehensive breakdown of all craftsmen wages for your construction project.',
        [LANGUAGES.MY]: 'Laporan ini menyediakan perincian komprehensif semua upah tukang untuk projek pembinaan anda.'
      },
      note2: {
        [LANGUAGES.EN]: 'Per square foot wages scale with the size of your house, while lumpsum amounts remain fixed.',
        [LANGUAGES.MY]: 'Upah per kaki persegi berubah mengikut saiz rumah anda, sementara amaun sekaligus kekal tetap.'
      },
      note3: {
        [LANGUAGES.EN]: 'Review phase allocations to better understand the distribution of labor costs across your project timeline.',
        [LANGUAGES.MY]: 'Semak peruntukan fasa untuk lebih memahami pengagihan kos buruh sepanjang garis masa projek anda.'
      },
      note4: {
        [LANGUAGES.EN]: 'Generated by RumahKira on',
        [LANGUAGES.MY]: 'Dijana oleh RumahKira pada'
      }
    }
  },
  
  // Material Calculator translations
  calculator: {
    title: {
      [LANGUAGES.EN]: 'Construction Material Calculator',
      [LANGUAGES.MY]: 'Kalkulator Bahan Pembinaan'
    },
    subtitle: {
      [LANGUAGES.EN]: 'Calculate quantities of materials needed for different construction elements.',
      [LANGUAGES.MY]: 'Kira kuantiti bahan yang diperlukan untuk pelbagai elemen pembinaan.'
    },
    selectUnit: {
      [LANGUAGES.EN]: 'Select measurement unit:',
      [LANGUAGES.MY]: 'Pilih unit ukuran:'
    },
    tabs: {
      concrete: {
        [LANGUAGES.EN]: 'Concrete Estimation',
        [LANGUAGES.MY]: 'Anggaran Konkrit'
      },
      brick: {
        [LANGUAGES.EN]: 'Brick Wall',
        [LANGUAGES.MY]: 'Dinding Bata'
      },
      paint: {
        [LANGUAGES.EN]: 'Paint',
        [LANGUAGES.MY]: 'Cat'
      },
      embankment: {
        [LANGUAGES.EN]: 'Embankment/Landfill',
        [LANGUAGES.MY]: 'Tanah Timbus/Tambak'
      },
      plastering: {
        [LANGUAGES.EN]: 'Plastering',
        [LANGUAGES.MY]: 'Melepa'
      },
      tiles: {
        [LANGUAGES.EN]: 'Tiles',
        [LANGUAGES.MY]: 'Jubin'
      },
      ceiling: {
        [LANGUAGES.EN]: 'Ceiling',
        [LANGUAGES.MY]: 'Siling'
      },
      wall: {
        [LANGUAGES.EN]: 'Brick Wall',
        [LANGUAGES.MY]: 'Dinding Bata'
      }
    },
    calculationMethod: {
      label: {
        [LANGUAGES.EN]: 'Calculation Method',
        [LANGUAGES.MY]: 'Kaedah Pengiraan'
      },
      dimensions: {
        [LANGUAGES.EN]: 'Calculate from Structure Dimensions',
        [LANGUAGES.MY]: 'Kira dari Dimensi Struktur'
      },
      volume: {
        [LANGUAGES.EN]: 'Enter Volume Directly (for Ready-Mix Order)',
        [LANGUAGES.MY]: 'Masukkan Isipadu Terus (untuk Pesanan Ready-Mix)'
      }
    },
    concreteUsage: {
      title: {
        [LANGUAGES.EN]: 'Concrete Usage Estimation for Construction',
        [LANGUAGES.MY]: 'Anggaran Penggunaan Konkrit untuk Pembinaan'
      },
      description: {
        [LANGUAGES.EN]: 'This calculator helps estimate concrete materials needed for various construction elements like foundations, columns, beams, or slabs.',
        [LANGUAGES.MY]: 'Kalkulator ini membantu menganggarkan bahan konkrit yang diperlukan untuk pelbagai elemen pembinaan seperti asas, tiang, rasuk, atau papak.'
      }
    },
    dimensions: {
      length: {
        [LANGUAGES.EN]: 'Length (Panjang)',
        [LANGUAGES.MY]: 'Panjang (Length)'
      },
      width: {
        [LANGUAGES.EN]: 'Width (Lebar)',
        [LANGUAGES.MY]: 'Lebar (Width)'
      },
      thickness: {
        [LANGUAGES.EN]: 'Thickness/Height (Tebal/Tinggi)',
        [LANGUAGES.MY]: 'Tebal/Tinggi (Thickness/Height)'
      },
      thicknessHelper: {
        [LANGUAGES.EN]: 'Thickness for slabs, height for columns/walls',
        [LANGUAGES.MY]: 'Ketebalan untuk papak, ketinggian untuk tiang/dinding'
      }
    },
    formula: {
      title: {
        [LANGUAGES.EN]: 'Standard Concrete Formula:',
        [LANGUAGES.MY]: 'Formula Konkrit Standard:'
      },
      formula: {
        [LANGUAGES.EN]: 'Volume (m³) = Length (m) × Width (m) × Thickness (m)',
        [LANGUAGES.MY]: 'Isipadu (m³) = Panjang (m) × Lebar (m) × Tebal (m)'
      },
      estimatedVolume: {
        [LANGUAGES.EN]: 'Estimated Volume:',
        [LANGUAGES.MY]: 'Anggaran Isipadu:'
      }
    },
    materialUnits: {
      title: {
        [LANGUAGES.EN]: 'Material Measurement Units:',
        [LANGUAGES.MY]: 'Unit Ukuran Bahan:'
      },
      sand: {
        [LANGUAGES.EN]: 'Sand Unit',
        [LANGUAGES.MY]: 'Unit Pasir'
      },
      gravel: {
        [LANGUAGES.EN]: 'Gravel Unit',
        [LANGUAGES.MY]: 'Unit Batu Baur'
      },
      batuPasir: {
        [LANGUAGES.EN]: 'Combined Sand + Stone',
        [LANGUAGES.MY]: 'Batu Pasir'
      },
      useBatuPasir: {
        [LANGUAGES.EN]: 'Use Combined Sand & Stone (Batu Pasir)',
        [LANGUAGES.MY]: 'Guna Batu Pasir (Pasir & Batu)'
      },
      batuPasirDesc: {
        [LANGUAGES.EN]: 'Use pre-mixed sand and stone instead of separate materials',
        [LANGUAGES.MY]: 'Guna campuran pasir dan batu baur sebagai satu bahan'
      },
      units: {
        cubicMeters: {
          [LANGUAGES.EN]: 'Cubic Meters (m³)',
          [LANGUAGES.MY]: 'Meter Padu (m³)'
        },
        kg: {
          [LANGUAGES.EN]: 'Kilograms (kg)',
          [LANGUAGES.MY]: 'Kilogram (kg)'
        },
        tonnes: {
          [LANGUAGES.EN]: 'Tonnes',
          [LANGUAGES.MY]: 'Tan'
        },
        wheelbarrows: {
          [LANGUAGES.EN]: 'Wheelbarrows',
          [LANGUAGES.MY]: 'Kereta Sorong'
        }
      }
    },
    brc: {
      includeLabel: {
        [LANGUAGES.EN]: 'Include BRC Mesh for Slab',
        [LANGUAGES.MY]: 'Sertakan Jaring BRC untuk Papak'
      },
      typeLabel: {
        [LANGUAGES.EN]: 'BRC Mesh Type',
        [LANGUAGES.MY]: 'Jenis Jaring BRC'
      },
      description: {
        [LANGUAGES.EN]: 'BRC mesh will strengthen the concrete slab and prevent cracking. Type A10 is commonly used for house floor slabs.',
        [LANGUAGES.MY]: 'Jaring BRC akan menguatkan papak konkrit dan mencegah keretakan. Jenis A10 biasanya digunakan untuk papak lantai rumah.'
      }
    },
    readyMix: {
      title: {
        [LANGUAGES.EN]: 'Ready-Mix Concrete Order Calculator',
        [LANGUAGES.MY]: 'Kalkulator Pesanan Konkrit Ready-Mix'
      },
      description: {
        [LANGUAGES.EN]: 'When ordering ready-mix concrete, you only need to specify the volume and mix ratio.',
        [LANGUAGES.MY]: 'Semasa memesan konkrit ready-mix, anda hanya perlu menentukan isipadu dan nisbah campuran.'
      },
      volumeLabel: {
        [LANGUAGES.EN]: 'Concrete Volume (m³)',
        [LANGUAGES.MY]: 'Isipadu Konkrit (m³)'
      },
      volumeHelper: {
        [LANGUAGES.EN]: 'Enter total volume of ready-mix concrete needed',
        [LANGUAGES.MY]: 'Masukkan jumlah isipadu konkrit ready-mix yang diperlukan'
      },
      formulaTitle: {
        [LANGUAGES.EN]: 'Ready-Mix Concrete Formula:',
        [LANGUAGES.MY]: 'Formula Konkrit Ready-Mix:'
      }
    },
    mixRatio: {
      label: {
        [LANGUAGES.EN]: 'Mix Ratio',
        [LANGUAGES.MY]: 'Nisbah Campuran'
      },
      standard: {
        [LANGUAGES.EN]: '1:2:4 (Standard Mix)',
        [LANGUAGES.MY]: '1:2:4 (Campuran Standard)'
      },
      rich: {
        [LANGUAGES.EN]: '1:1.5:3 (Richer Mix)',
        [LANGUAGES.MY]: '1:1.5:3 (Campuran Lebih Kaya)'
      },
      lean: {
        [LANGUAGES.EN]: '1:3:5 (Leaner Mix)',
        [LANGUAGES.MY]: '1:3:5 (Campuran Lebih Cair)'
      }
    },
    constructionType: {
      label: {
        [LANGUAGES.EN]: 'Construction Type',
        [LANGUAGES.MY]: 'Jenis Pembinaan'
      },
      general: {
        [LANGUAGES.EN]: 'General Construction',
        [LANGUAGES.MY]: 'Pembinaan Umum'
      },
      slab: {
        [LANGUAGES.EN]: 'Concrete Slab/Floor',
        [LANGUAGES.MY]: 'Papak/Lantai Konkrit'
      },
      foundation: {
        [LANGUAGES.EN]: 'Foundation',
        [LANGUAGES.MY]: 'Asas'
      },
      column: {
        [LANGUAGES.EN]: 'Columns/Pillars',
        [LANGUAGES.MY]: 'Tiang/Pillar'
      },
      beam: {
        [LANGUAGES.EN]: 'Beams',
        [LANGUAGES.MY]: 'Rasuk'
      }
    },
    calculateButton: {
      [LANGUAGES.EN]: 'Calculate Materials',
      [LANGUAGES.MY]: 'Kira Bahan'
    },
    results: {
      title: {
        [LANGUAGES.EN]: 'Concrete Calculation Results',
        [LANGUAGES.MY]: 'Keputusan Pengiraan Konkrit'
      },
      propertyHeader: {
        [LANGUAGES.EN]: 'Property',
        [LANGUAGES.MY]: 'Ciri'
      },
      valueHeader: {
        [LANGUAGES.EN]: 'Value',
        [LANGUAGES.MY]: 'Nilai'
      },
      readyMixVolume: {
        [LANGUAGES.EN]: 'Ready-Mix Volume',
        [LANGUAGES.MY]: 'Isipadu Ready-Mix'
      },
      dimensions: {
        [LANGUAGES.EN]: 'Dimensions',
        [LANGUAGES.MY]: 'Dimensi'
      },
      totalConcreteVolume: {
        [LANGUAGES.EN]: 'Total Concrete Volume',
        [LANGUAGES.MY]: 'Jumlah Isipadu Konkrit'
      },
      mixRatio: {
        [LANGUAGES.EN]: 'Mix Ratio (Cement:Sand:Gravel)',
        [LANGUAGES.MY]: 'Nisbah Campuran (Simen:Pasir:Batu)'
      },
      area: {
        [LANGUAGES.EN]: 'Area',
        [LANGUAGES.MY]: 'Keluasan'
      },
      volume: {
        [LANGUAGES.EN]: 'Volume',
        [LANGUAGES.MY]: 'Isipadu'
      },
      readyMixOption: {
        title: {
          [LANGUAGES.EN]: 'Ready-Mix Option',
          [LANGUAGES.MY]: 'Pilihan Ready-Mix'
        },
        orderText: {
          [LANGUAGES.EN]: 'Order',
          [LANGUAGES.MY]: 'Pesan'
        },
        cubicMeters: {
          [LANGUAGES.EN]: 'cubic meters of',
          [LANGUAGES.MY]: 'meter padu'
        },
        mixRatio: {
          [LANGUAGES.EN]: 'mix ratio',
          [LANGUAGES.MY]: 'nisbah campuran'
        },
        benefits: {
          [LANGUAGES.EN]: 'Benefits: Consistent quality, saves labor, faster work progress.',
          [LANGUAGES.MY]: 'Kelebihan: Kualiti yang konsisten, menjimatkan tenaga kerja, kemajuan kerja lebih cepat.'
        }
      },
      manualOption: {
        title: {
          [LANGUAGES.EN]: 'Manual Mixing Option',
          [LANGUAGES.MY]: 'Pilihan Campuran Manual'
        },
        wastage: {
          [LANGUAGES.EN]: 'Includes',
          [LANGUAGES.MY]: 'Termasuk'
        },
        additional: {
          [LANGUAGES.EN]: 'additional for wastage',
          [LANGUAGES.MY]: 'tambahan untuk pembaziran'
        }
      },
      materials: {
        cement: {
          [LANGUAGES.EN]: 'Cement',
          [LANGUAGES.MY]: 'Simen'
        },
        bags: {
          [LANGUAGES.EN]: 'bags (50kg)',
          [LANGUAGES.MY]: 'beg (50kg)'
        },
        batuPasir: {
          [LANGUAGES.EN]: 'Combined Sand & Stone',
          [LANGUAGES.MY]: 'Campuran Pasir & Batu'
        },
        sand: {
          [LANGUAGES.EN]: 'Sand',
          [LANGUAGES.MY]: 'Pasir'
        },
        gravel: {
          [LANGUAGES.EN]: 'Gravel/Aggregate',
          [LANGUAGES.MY]: 'Batu Baur/Aggregat'
        },
        note: {
          [LANGUAGES.EN]: 'Note: Manual mixing requires clean water, proper mixing equipment, and careful curing for optimal results.',
          [LANGUAGES.MY]: 'Nota: Pencampuran manual memerlukan air bersih, peralatan mencampur yang sesuai, dan penjagaan teliti untuk hasil optimum.'
        },
        brc: {
          [LANGUAGES.EN]: 'BRC Mesh',
          [LANGUAGES.MY]: 'Jaring BRC'
        }
      },
      comparison: {
        title: {
          [LANGUAGES.EN]: 'Ready-Mix vs. Manual Mixing',
          [LANGUAGES.MY]: 'Ready-Mix lwn. Pencampuran Manual'
        },
        readyMixTitle: {
          [LANGUAGES.EN]: 'Ready-Mix Concrete Advantages',
          [LANGUAGES.MY]: 'Kelebihan Konkrit Ready-Mix'
        },
        readyMixItems: {
          point1: {
            [LANGUAGES.EN]: 'Consistent quality and strength',
            [LANGUAGES.MY]: 'Kualiti dan kekuatan yang konsisten'
          },
          point2: {
            [LANGUAGES.EN]: 'Reduces labor requirements',
            [LANGUAGES.MY]: 'Mengurangkan keperluan tenaga kerja'
          },
          point3: {
            [LANGUAGES.EN]: 'Faster construction time',
            [LANGUAGES.MY]: 'Masa pembinaan lebih cepat'
          },
          point4: {
            [LANGUAGES.EN]: 'Less material wastage',
            [LANGUAGES.MY]: 'Kurang pembaziran bahan'
          },
          point5: {
            [LANGUAGES.EN]: 'Better for large volume projects',
            [LANGUAGES.MY]: 'Lebih baik untuk projek berisipadu besar'
          }
        },
        manualTitle: {
          [LANGUAGES.EN]: 'Manual Mixing Advantages',
          [LANGUAGES.MY]: 'Kelebihan Pencampuran Manual'
        },
        manualItems: {
          point1: {
            [LANGUAGES.EN]: 'Lower cost for small projects',
            [LANGUAGES.MY]: 'Kos lebih rendah untuk projek kecil'
          },
          point2: {
            [LANGUAGES.EN]: 'No minimum order quantity',
            [LANGUAGES.MY]: 'Tiada kuantiti pesanan minimum'
          },
          point3: {
            [LANGUAGES.EN]: 'Work can be done in stages',
            [LANGUAGES.MY]: 'Kerja boleh dilakukan secara berperingkat'
          },
          point4: {
            [LANGUAGES.EN]: 'More flexibility with timing',
            [LANGUAGES.MY]: 'Lebih fleksibel dengan masa'
          },
          point5: {
            [LANGUAGES.EN]: 'Better for remote locations',
            [LANGUAGES.MY]: 'Lebih baik untuk lokasi terpencil'
          }
        }
      },
      brcResults: {
        title: {
          [LANGUAGES.EN]: 'BRC Mesh Requirements',
          [LANGUAGES.MY]: 'Keperluan Jaring BRC'
        },
        type: {
          [LANGUAGES.EN]: 'BRC Type',
          [LANGUAGES.MY]: 'Jenis BRC'
        },
        sheets: {
          [LANGUAGES.EN]: 'BRC Sheets Required',
          [LANGUAGES.MY]: 'Kepingan BRC Diperlukan'
        },
        sheetsSuffix: {
          [LANGUAGES.EN]: 'sheets of',
          [LANGUAGES.MY]: 'keping'
        },
        area: {
          [LANGUAGES.EN]: 'Total BRC Area',
          [LANGUAGES.MY]: 'Jumlah Keluasan BRC'
        }
      }
    },
    embankment: {
      title: {
        [LANGUAGES.EN]: 'Embankment/Landfill Calculation',
        [LANGUAGES.MY]: 'Pengiraan Tanah Timbus/Tambak'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate the volume of soil needed for embankment or landfill works.',
        [LANGUAGES.MY]: 'Kira jumlah tanah yang diperlukan untuk kerja-kerja timbusan atau tambak.'
      },
      length: {
        [LANGUAGES.EN]: 'Length (Panjang)',
        [LANGUAGES.MY]: 'Panjang (Length)'
      },
      width: {
        [LANGUAGES.EN]: 'Width (Lebar)',
        [LANGUAGES.MY]: 'Lebar (Width)'
      },
      height: {
        [LANGUAGES.EN]: 'Height (Tinggi)',
        [LANGUAGES.MY]: 'Tinggi (Height)'
      },
      heightHelper: {
        [LANGUAGES.EN]: 'Average height/thickness of the embankment',
        [LANGUAGES.MY]: 'Purata ketinggian/ketebalan tambak'
      },
      compactionFactor: {
        [LANGUAGES.EN]: 'Compaction Factor',
        [LANGUAGES.MY]: 'Faktor Pemadatan'
      },
      compactionFactorHelper: {
        [LANGUAGES.EN]: 'Higher value means more soil needed (typically 1.2-1.4)',
        [LANGUAGES.MY]: 'Nilai yang lebih tinggi bermakna lebih banyak tanah diperlukan (biasanya 1.2-1.4)'
      },
      results: {
        title: {
          [LANGUAGES.EN]: 'Embankment Material Requirements',
          [LANGUAGES.MY]: 'Keperluan Bahan Tambak'
        },
        area: {
          [LANGUAGES.EN]: 'Area to Fill',
          [LANGUAGES.MY]: 'Keluasan untuk Diisi'
        },
        rawVolume: {
          [LANGUAGES.EN]: 'Raw Volume',
          [LANGUAGES.MY]: 'Isipadu Mentah'
        },
        compactedVolume: {
          [LANGUAGES.EN]: 'Compacted Volume (with factor)',
          [LANGUAGES.MY]: 'Isipadu Terpadat (dengan faktor)'
        },
        truckloads: {
          [LANGUAGES.EN]: 'Estimated Truckloads',
          [LANGUAGES.MY]: 'Anggaran Muatan Lori'
        },
        truckloadNote: {
          [LANGUAGES.EN]: 'Based on standard 10m³ dump truck capacity',
          [LANGUAGES.MY]: 'Berdasarkan kapasiti lori pembuangan standard 10m³'
        }
      }
    },
    paint: {
      title: {
        [LANGUAGES.EN]: 'Paint Calculator',
        [LANGUAGES.MY]: 'Kalkulator Cat'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate the amount of paint needed for your walls and ceiling.',
        [LANGUAGES.MY]: 'Kira jumlah cat yang diperlukan untuk dinding dan siling anda.'
      },
      wallLength: {
        [LANGUAGES.EN]: 'Wall Length',
        [LANGUAGES.MY]: 'Panjang Dinding'
      },
      wallHeight: {
        [LANGUAGES.EN]: 'Wall Height',
        [LANGUAGES.MY]: 'Tinggi Dinding'
      },
      coverage: {
        [LANGUAGES.EN]: 'Paint Coverage',
        [LANGUAGES.MY]: 'Liputan Cat'
      },
      coverageHelper: {
        [LANGUAGES.EN]: 'Square meters covered per liter of paint',
        [LANGUAGES.MY]: 'Meter persegi yang diliputi per liter cat'
      },
      coats: {
        [LANGUAGES.EN]: 'Number of Coats',
        [LANGUAGES.MY]: 'Bilangan Lapisan'
      },
      coatsHelper: {
        [LANGUAGES.EN]: 'Recommended: 2 coats for new walls, 1 coat for repainting',
        [LANGUAGES.MY]: 'Disyorkan: 2 lapisan untuk dinding baru, 1 lapisan untuk mengecat semula'
      },
      paintType: {
        [LANGUAGES.EN]: 'Paint Type',
        [LANGUAGES.MY]: 'Jenis Cat'
      },
      types: {
        emulsion: {
          [LANGUAGES.EN]: 'Emulsion (Water-based)',
          [LANGUAGES.MY]: 'Emulsi (Berasaskan Air)'
        },
        gloss: {
          [LANGUAGES.EN]: 'Gloss (Oil-based)',
          [LANGUAGES.MY]: 'Kilat (Berasaskan Minyak)'
        },
        primer: {
          [LANGUAGES.EN]: 'Primer/Undercoat',
          [LANGUAGES.MY]: 'Primer/Lapisan Dasar'
        }
      },
      results: {
        area: {
          [LANGUAGES.EN]: 'Total Wall Area',
          [LANGUAGES.MY]: 'Jumlah Keluasan Dinding'
        },
        paintNeeded: {
          [LANGUAGES.EN]: 'Paint Needed',
          [LANGUAGES.MY]: 'Cat Diperlukan'
        },
        recommendedCans: {
          [LANGUAGES.EN]: 'Recommended Cans',
          [LANGUAGES.MY]: 'Tin yang Disyorkan'
        },
        primerNeeded: {
          [LANGUAGES.EN]: 'Primer Needed',
          [LANGUAGES.MY]: 'Primer Diperlukan'
        },
        liters: {
          [LANGUAGES.EN]: 'liters',
          [LANGUAGES.MY]: 'liter'
        },
        cans: {
          [LANGUAGES.EN]: 'paint cans',
          [LANGUAGES.MY]: 'tin cat'
        },
        standard: {
          [LANGUAGES.EN]: '(standard 5L can)',
          [LANGUAGES.MY]: '(tin standard 5L)'
        },
        primer: {
          [LANGUAGES.EN]: 'Primer Needed (for new surfaces)',
          [LANGUAGES.MY]: 'Primer Diperlukan (untuk permukaan baru)'
        },
        extras: {
          [LANGUAGES.EN]: 'Additional Items',
          [LANGUAGES.MY]: 'Item Tambahan'
        },
        brushes: {
          [LANGUAGES.EN]: 'Brushes/Rollers',
          [LANGUAGES.MY]: 'Berus/Roller'
        },
        tape: {
          [LANGUAGES.EN]: 'Masking Tape',
          [LANGUAGES.MY]: 'Pita Masking'
        },
        plastic: {
          [LANGUAGES.EN]: 'Plastic Sheets',
          [LANGUAGES.MY]: 'Kepingan Plastik'
        },
        thinner: {
          [LANGUAGES.EN]: 'Paint Thinner (for oil-based)',
          [LANGUAGES.MY]: 'Pencair Cat (untuk berasaskan minyak)'
        }
      }
    },
    plastering: {
      title: {
        [LANGUAGES.EN]: 'Plastering Calculator',
        [LANGUAGES.MY]: 'Kalkulator Melepa'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate how much cement and sand you need for plastering walls',
        [LANGUAGES.MY]: 'Kira berapa banyak simen dan pasir yang anda perlukan untuk melepa dinding'
      },
      thickness: {
        [LANGUAGES.EN]: 'Plaster Thickness',
        [LANGUAGES.MY]: 'Ketebalan Lepaan'
      },
      thicknessHelper: {
        [LANGUAGES.EN]: 'Typical range: 10-20mm',
        [LANGUAGES.MY]: 'Julat biasa: 10-20mm'
      },
      wastage: {
        [LANGUAGES.EN]: 'Wastage (%)',
        [LANGUAGES.MY]: 'Pembaziran (%)'
      },
      wastageHelper: {
        [LANGUAGES.EN]: 'Typical: 15% wastage',
        [LANGUAGES.MY]: 'Biasa: 15% pembaziran'
      },
      mixRatio: {
        [LANGUAGES.EN]: 'Mix Ratio',
        [LANGUAGES.MY]: 'Nisbah Campuran'
      },
      unitDescription: {
        [LANGUAGES.EN]: 'Select your preferred unit for sand measurement',
        [LANGUAGES.MY]: 'Pilih unit pilihan anda untuk pengukuran pasir'
      },
      ratioOptions: {
        r14: {
          [LANGUAGES.EN]: '1:4 (Strong - High Traffic)',
          [LANGUAGES.MY]: '1:4 (Kuat - Trafik Tinggi)'
        },
        r15: {
          [LANGUAGES.EN]: '1:5 (Medium)',
          [LANGUAGES.MY]: '1:5 (Sederhana)'
        },
        r16: {
          [LANGUAGES.EN]: '1:6 (Standard)',
          [LANGUAGES.MY]: '1:6 (Standard)'
        }
      },
      results: {
        title: {
          [LANGUAGES.EN]: 'Plastering Material Requirements',
          [LANGUAGES.MY]: 'Keperluan Bahan Melepa'
        },
        wallArea: {
          [LANGUAGES.EN]: 'Wall Area',
          [LANGUAGES.MY]: 'Keluasan Dinding'
        },
        plasterVolume: {
          [LANGUAGES.EN]: 'Plaster Volume (without wastage)',
          [LANGUAGES.MY]: 'Isipadu Lepaan (tanpa pembaziran)'
        },
        totalVolume: {
          [LANGUAGES.EN]: 'Total Volume (with wastage)',
          [LANGUAGES.MY]: 'Jumlah Isipadu (dengan pembaziran)'
        },
        cement: {
          [LANGUAGES.EN]: 'Cement Required',
          [LANGUAGES.MY]: 'Simen Diperlukan'
        },
        sand: {
          [LANGUAGES.EN]: 'Sand Required',
          [LANGUAGES.MY]: 'Pasir Diperlukan'
        }
      }
    },
    tiles: {
      title: {
        [LANGUAGES.EN]: 'Tile Material Calculation',
        [LANGUAGES.MY]: 'Pengiraan Bahan Jubin'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate tile quantities and adhesive needed for flooring or wall tiling.',
        [LANGUAGES.MY]: 'Kira kuantiti jubin dan pelekat yang diperlukan untuk lantai atau dinding.'
      },
      tileSize: {
        [LANGUAGES.EN]: 'Tile Size',
        [LANGUAGES.MY]: 'Saiz Jubin'
      },
      tileSizeHelper: {
        [LANGUAGES.EN]: 'Common sizes: 30x30cm, 60x60cm, 30x60cm',
        [LANGUAGES.MY]: 'Saiz biasa: 30x30cm, 60x60cm, 30x60cm'
      },
      tileLength: {
        [LANGUAGES.EN]: 'Tile Length',
        [LANGUAGES.MY]: 'Panjang Jubin'
      },
      tileWidth: {
        [LANGUAGES.EN]: 'Tile Width',
        [LANGUAGES.MY]: 'Lebar Jubin'
      },
      wastage: {
        [LANGUAGES.EN]: 'Wastage (%)',
        [LANGUAGES.MY]: 'Pembaziran (%)'
      },
      wastageHelper: {
        [LANGUAGES.EN]: 'Additional tiles for cuts and breakage (recommended: 10%)',
        [LANGUAGES.MY]: 'Jubin tambahan untuk potongan dan kerosakan (dicadangkan: 10%)'
      },
      commonTileSizes: {
        size1: {
          [LANGUAGES.EN]: '30cm x 30cm (Standard)',
          [LANGUAGES.MY]: '30cm x 30cm (Standard)'
        },
        size2: {
          [LANGUAGES.EN]: '60cm x 60cm (Large)',
          [LANGUAGES.MY]: '60cm x 60cm (Besar)'
        },
        size3: {
          [LANGUAGES.EN]: '30cm x 60cm (Rectangular)',
          [LANGUAGES.MY]: '30cm x 60cm (Segi Empat Tepat)'
        },
        size4: {
          [LANGUAGES.EN]: '20cm x 20cm (Small)',
          [LANGUAGES.MY]: '20cm x 20cm (Kecil)'
        },
        custom: {
          [LANGUAGES.EN]: 'Custom Size',
          [LANGUAGES.MY]: 'Saiz Khas'
        }
      },
      adhesiveAmount: {
        [LANGUAGES.EN]: 'Adhesive per m²',
        [LANGUAGES.MY]: 'Pelekat per m²'
      },
      adhesiveHelper: {
        [LANGUAGES.EN]: 'Amount of adhesive needed per square meter (kg)',
        [LANGUAGES.MY]: 'Jumlah pelekat yang diperlukan per meter persegi (kg)'
      },
      groutAmount: {
        [LANGUAGES.EN]: 'Grout per m²',
        [LANGUAGES.MY]: 'Grout per m²'
      },
      groutHelper: {
        [LANGUAGES.EN]: 'Amount of grout needed per square meter (kg)',
        [LANGUAGES.MY]: 'Jumlah grout yang diperlukan per meter persegi (kg)'
      },
      results: {
        title: {
          [LANGUAGES.EN]: 'Tile Material Requirements',
          [LANGUAGES.MY]: 'Keperluan Bahan Jubin'
        },
        area: {
          [LANGUAGES.EN]: 'Area to Tile',
          [LANGUAGES.MY]: 'Keluasan untuk Jubin'
        },
        tilesNeeded: {
          [LANGUAGES.EN]: 'Tiles Needed (without wastage)',
          [LANGUAGES.MY]: 'Jubin Diperlukan (tanpa pembaziran)'
        },
        totalTiles: {
          [LANGUAGES.EN]: 'Total Tiles (with wastage)',
          [LANGUAGES.MY]: 'Jumlah Jubin (dengan pembaziran)'
        },
        adhesive: {
          [LANGUAGES.EN]: 'Adhesive Required',
          [LANGUAGES.MY]: 'Pelekat Diperlukan'
        },
        adhesiveBags: {
          [LANGUAGES.EN]: 'Adhesive Bags (20kg each)',
          [LANGUAGES.MY]: 'Beg Pelekat (20kg setiap satu)'
        },
        grout: {
          [LANGUAGES.EN]: 'Grout Required',
          [LANGUAGES.MY]: 'Grout Diperlukan'
        },
        groutBags: {
          [LANGUAGES.EN]: 'Grout Bags',
          [LANGUAGES.MY]: 'Beg Grout'
        }
      }
    },
    ceiling: {
      title: {
        [LANGUAGES.EN]: 'Ceiling Material Calculation',
        [LANGUAGES.MY]: 'Pengiraan Bahan Siling'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate materials needed for different types of ceiling installations.',
        [LANGUAGES.MY]: 'Kira bahan yang diperlukan untuk pemasangan pelbagai jenis siling.'
      },
      ceilingType: {
        [LANGUAGES.EN]: 'Ceiling Type',
        [LANGUAGES.MY]: 'Jenis Siling'
      },
      types: {
        gypsum: {
          [LANGUAGES.EN]: 'Gypsum Board',
          [LANGUAGES.MY]: 'Papan Gypsum'
        },
        pvc: {
          [LANGUAGES.EN]: 'PVC Panels',
          [LANGUAGES.MY]: 'Panel PVC'
        },
        wood: {
          [LANGUAGES.EN]: 'Wooden Panels',
          [LANGUAGES.MY]: 'Panel Kayu'
        }
      },
      wastage: {
        [LANGUAGES.EN]: 'Wastage (%)',
        [LANGUAGES.MY]: 'Pembaziran (%)'
      },
      wastageHelper: {
        [LANGUAGES.EN]: 'Additional material for cuts and wastage (recommended: 7%)',
        [LANGUAGES.MY]: 'Bahan tambahan untuk potongan dan pembaziran (dicadangkan: 7%)'
      },
      results: {
        title: {
          [LANGUAGES.EN]: 'Ceiling Material Requirements',
          [LANGUAGES.MY]: 'Keperluan Bahan Siling'
        },
        area: {
          [LANGUAGES.EN]: 'Ceiling Area',
          [LANGUAGES.MY]: 'Keluasan Siling'
        },
        areaWithWastage: {
          [LANGUAGES.EN]: 'Area with Wastage',
          [LANGUAGES.MY]: 'Keluasan dengan Pembaziran'
        }
      },
      gypsum: {
        boardsNeeded: {
          [LANGUAGES.EN]: 'Gypsum Boards Required',
          [LANGUAGES.MY]: 'Papan Gypsum Diperlukan'
        },
        boardSize: {
          [LANGUAGES.EN]: 'Standard board size: 1.2m x 2.4m',
          [LANGUAGES.MY]: 'Saiz papan standard: 1.2m x 2.4m'
        },
        mainTee: {
          [LANGUAGES.EN]: 'Main Tee Length',
          [LANGUAGES.MY]: 'Panjang Main Tee'
        },
        crossTee: {
          [LANGUAGES.EN]: 'Cross Tee Length',
          [LANGUAGES.MY]: 'Panjang Cross Tee'
        },
        perimeterTrim: {
          [LANGUAGES.EN]: 'Perimeter Trim Length',
          [LANGUAGES.MY]: 'Panjang Trim Perimeter'
        },
        screws: {
          [LANGUAGES.EN]: 'Screws Required',
          [LANGUAGES.MY]: 'Skru Diperlukan'
        },
        joiningCompound: {
          [LANGUAGES.EN]: 'Joining Compound',
          [LANGUAGES.MY]: 'Sebatian Penyambung'
        }
      },
      pvc: {
        panelsNeeded: {
          [LANGUAGES.EN]: 'PVC Panels Required',
          [LANGUAGES.MY]: 'Panel PVC Diperlukan'
        },
        panelSize: {
          [LANGUAGES.EN]: 'Standard panel size: 25cm x 3m',
          [LANGUAGES.MY]: 'Saiz panel standard: 25cm x 3m'
        },
        mainTee: {
          [LANGUAGES.EN]: 'Main Track Length',
          [LANGUAGES.MY]: 'Panjang Track Utama'
        },
        crossTee: {
          [LANGUAGES.EN]: 'Cross Track Length',
          [LANGUAGES.MY]: 'Panjang Track Silang'
        },
        trims: {
          [LANGUAGES.EN]: 'Trims Length',
          [LANGUAGES.MY]: 'Panjang Trim'
        }
      },
      wood: {
        panelsNeeded: {
          [LANGUAGES.EN]: 'Wooden Panels Required',
          [LANGUAGES.MY]: 'Panel Kayu Diperlukan'
        },
        panelSize: {
          [LANGUAGES.EN]: 'Standard panel size: 10cm x 3m',
          [LANGUAGES.MY]: 'Saiz panel standard: 10cm x 3m'
        },
        battens: {
          [LANGUAGES.EN]: 'Wooden Battens Length',
          [LANGUAGES.MY]: 'Panjang Bilah Kayu'
        },
        screws: {
          [LANGUAGES.EN]: 'Screws Required',
          [LANGUAGES.MY]: 'Skru Diperlukan'
        },
        cornerTrims: {
          [LANGUAGES.EN]: 'Corner Trims Length',
          [LANGUAGES.MY]: 'Panjang Trim Sudut'
        }
      }
    },
    wall: {
      title: {
        [LANGUAGES.EN]: 'Brick Wall Calculator',
        [LANGUAGES.MY]: 'Kalkulator Dinding Bata'
      },
      description: {
        [LANGUAGES.EN]: 'Calculate the number of bricks and mortar needed for wall construction.',
        [LANGUAGES.MY]: 'Kira bilangan bata dan mortar yang diperlukan untuk pembinaan dinding.'
      },
      length: {
        [LANGUAGES.EN]: 'Wall Length',
        [LANGUAGES.MY]: 'Panjang Dinding'
      },
      height: {
        [LANGUAGES.EN]: 'Wall Height',
        [LANGUAGES.MY]: 'Tinggi Dinding'
      },
      brickLength: {
        [LANGUAGES.EN]: 'Brick Length',
        [LANGUAGES.MY]: 'Panjang Bata'
      },
      brickHeight: {
        [LANGUAGES.EN]: 'Brick Height',
        [LANGUAGES.MY]: 'Tinggi Bata'
      },
      brickSize: {
        [LANGUAGES.EN]: 'Brick Size',
        [LANGUAGES.MY]: 'Saiz Bata'
      },
      mortar: {
        [LANGUAGES.EN]: 'Mortar Thickness',
        [LANGUAGES.MY]: 'Ketebalan Mortar'
      },
      mortarRatio: {
        [LANGUAGES.EN]: 'Mortar Mix Ratio',
        [LANGUAGES.MY]: 'Nisbah Campuran Mortar'
      },
      bricks: {
        [LANGUAGES.EN]: 'Number of Bricks',
        [LANGUAGES.MY]: 'Bilangan Bata'
      },
      area: {
        [LANGUAGES.EN]: 'Wall Area',
        [LANGUAGES.MY]: 'Keluasan Dinding'
      },
      sand: {
        [LANGUAGES.EN]: 'Sand for Mortar',
        [LANGUAGES.MY]: 'Pasir untuk Mortar'
      },
      mortarVolume: {
        [LANGUAGES.EN]: 'Mortar Volume',
        [LANGUAGES.MY]: 'Isipadu Mortar'
      },
      standardSize: {
        [LANGUAGES.EN]: 'Standard Size (230 x 110 x 70 mm)',
        [LANGUAGES.MY]: 'Saiz Standard (230 x 110 x 70 mm)'
      },
      customSize: {
        [LANGUAGES.EN]: 'Custom Size',
        [LANGUAGES.MY]: 'Saiz Khas'
      }
    }
  },
  units: {
    meters: {
      [LANGUAGES.EN]: 'Meters',
      [LANGUAGES.MY]: 'Meter'
    },
    centimeters: {
      [LANGUAGES.EN]: 'Centimeters',
      [LANGUAGES.MY]: 'Sentimeter'
    },
    feet: {
      [LANGUAGES.EN]: 'Feet',
      [LANGUAGES.MY]: 'Kaki'
    },
    inches: {
      [LANGUAGES.EN]: 'Inches',
      [LANGUAGES.MY]: 'Inci'
    }
  },

  // Landing Page translations
  landing: {
    heroTitle: {
      [LANGUAGES.EN]: 'Plan Your Dream Home Budget',
      [LANGUAGES.MY]: 'Rancang Bajet Rumah Impian Anda'
    },
    heroSubtitle: {
      [LANGUAGES.EN]: 'with Confidence',
      [LANGUAGES.MY]: 'dengan Yakin'
    },
    heroDescription: {
      [LANGUAGES.EN]: 'Professional construction cost estimator for Malaysian homes. Calculate materials, estimate budgets, and generate detailed reports.',
      [LANGUAGES.MY]: 'Penganggar kos pembinaan profesional untuk rumah di Malaysia. Kira bahan, anggarkan bajet, dan jana laporan terperinci.'
    },
    tryFree: {
      [LANGUAGES.EN]: 'Try Free - 2 Calculations',
      [LANGUAGES.MY]: 'Cuba Percuma - 2 Pengiraan'
    },
    signUp: {
      [LANGUAGES.EN]: 'Sign Up',
      [LANGUAGES.MY]: 'Daftar'
    },
    signIn: {
      [LANGUAGES.EN]: 'Sign In',
      [LANGUAGES.MY]: 'Log Masuk'
    },
    featuresTitle: {
      [LANGUAGES.EN]: 'Everything You Need',
      [LANGUAGES.MY]: 'Semua Yang Anda Perlukan'
    },
    featuresSubtitle: {
      [LANGUAGES.EN]: 'Powerful tools to plan your construction project',
      [LANGUAGES.MY]: 'Alat berkuasa untuk merancang projek pembinaan anda'
    },
    howItWorksTitle: {
      [LANGUAGES.EN]: 'How It Works',
      [LANGUAGES.MY]: 'Cara Ia Berfungsi'
    },
    pricingTitle: {
      [LANGUAGES.EN]: 'Simple Pricing',
      [LANGUAGES.MY]: 'Harga Mudah'
    }
  },

  // Conversion Modal translations
  conversionModal: {
    title: {
      [LANGUAGES.EN]: 'Free Estimates Used',
      [LANGUAGES.MY]: 'Anggaran Percuma Habis'
    },
    subtitle: {
      [LANGUAGES.EN]: "You've used your 2 free estimates",
      [LANGUAGES.MY]: 'Anda telah menggunakan 2 anggaran percuma anda'
    },
    description: {
      [LANGUAGES.EN]: 'Get an Access Pass to unlock more features:',
      [LANGUAGES.MY]: 'Dapatkan Pas Akses untuk membuka lebih banyak ciri:'
    },
    benefit1: {
      [LANGUAGES.EN]: 'Unlimited estimates',
      [LANGUAGES.MY]: 'Anggaran tanpa had'
    },
    benefit2: {
      [LANGUAGES.EN]: 'Save and access projects anytime',
      [LANGUAGES.MY]: 'Simpan dan akses projek bila-bila masa'
    },
    benefit3: {
      [LANGUAGES.EN]: 'Export detailed PDF reports',
      [LANGUAGES.MY]: 'Eksport laporan PDF terperinci'
    },
    signUpNow: {
      [LANGUAGES.EN]: 'Get Access Pass',
      [LANGUAGES.MY]: 'Dapatkan Pas Akses'
    },
    maybeLater: {
      [LANGUAGES.EN]: 'Maybe Later',
      [LANGUAGES.MY]: 'Mungkin Nanti'
    }
  },

  // Usage Banner translations
  usageBanner: {
    freeTrialPrefix: {
      [LANGUAGES.EN]: 'Free:',
      [LANGUAGES.MY]: 'Percuma:'
    },
    exhaustedText: {
      [LANGUAGES.EN]: 'Free estimates used',
      [LANGUAGES.MY]: 'Anggaran percuma habis'
    },
    signUpText: {
      [LANGUAGES.EN]: 'Get Access Pass for unlimited access',
      [LANGUAGES.MY]: 'Dapatkan Pas Akses untuk akses tanpa had'
    }
  },

  // Access Pass translations
  accessPass: {
    title: {
      [LANGUAGES.EN]: 'Access Pass',
      [LANGUAGES.MY]: 'Pas Akses'
    },
    getAccess: {
      [LANGUAGES.EN]: 'Get Access Pass',
      [LANGUAGES.MY]: 'Dapatkan Pas Akses'
    },
    currentPass: {
      [LANGUAGES.EN]: 'Current Pass',
      [LANGUAGES.MY]: 'Pas Semasa'
    },
    passActive: {
      [LANGUAGES.EN]: 'Pass Active',
      [LANGUAGES.MY]: 'Pas Aktif'
    },
    passExpired: {
      [LANGUAGES.EN]: 'Pass Expired',
      [LANGUAGES.MY]: 'Pas Tamat'
    },
    expiresIn: {
      [LANGUAGES.EN]: 'Expires in',
      [LANGUAGES.MY]: 'Tamat dalam'
    },
    expired: {
      [LANGUAGES.EN]: 'Expired',
      [LANGUAGES.MY]: 'Tamat tempoh'
    },
    oneTimePayment: {
      [LANGUAGES.EN]: 'One-time payment - no recurring charges',
      [LANGUAGES.MY]: 'Bayaran sekali - tiada caj berulang'
    },
    recommended: {
      [LANGUAGES.EN]: 'Recommended',
      [LANGUAGES.MY]: 'Disyorkan'
    },
    freeUser: {
      [LANGUAGES.EN]: 'Free User',
      [LANGUAGES.MY]: 'Pengguna Percuma'
    },
    // Pass types
    freePlan: {
      [LANGUAGES.EN]: 'Free',
      [LANGUAGES.MY]: 'Percuma'
    },
    quickPass: {
      [LANGUAGES.EN]: 'Quick Pass',
      [LANGUAGES.MY]: 'Pas Pantas'
    },
    planningPass: {
      [LANGUAGES.EN]: 'Planning Pass',
      [LANGUAGES.MY]: 'Pas Perancangan'
    },
    builderPass: {
      [LANGUAGES.EN]: 'Builder Pass',
      [LANGUAGES.MY]: 'Pas Pembina'
    },
    // Features
    basicEstimates: {
      [LANGUAGES.EN]: 'Basic Estimates',
      [LANGUAGES.MY]: 'Anggaran Asas'
    },
    unlimitedEstimates: {
      [LANGUAGES.EN]: 'Unlimited Estimates',
      [LANGUAGES.MY]: 'Anggaran Tanpa Had'
    },
    saveProjects: {
      [LANGUAGES.EN]: 'Save Projects',
      [LANGUAGES.MY]: 'Simpan Projek'
    },
    materialEstimator: {
      [LANGUAGES.EN]: 'Material Estimator',
      [LANGUAGES.MY]: 'Penganggar Bahan'
    },
    compareScenarios: {
      [LANGUAGES.EN]: 'Compare Scenarios',
      [LANGUAGES.MY]: 'Bandingkan Senario'
    },
    pdfExport: {
      [LANGUAGES.EN]: 'PDF Export',
      [LANGUAGES.MY]: 'Eksport PDF'
    },
    fullBreakdown: {
      [LANGUAGES.EN]: 'Full Breakdown',
      [LANGUAGES.MY]: 'Pecahan Penuh'
    },
    // Duration text
    hours: {
      [LANGUAGES.EN]: 'hours',
      [LANGUAGES.MY]: 'jam'
    },
    days: {
      [LANGUAGES.EN]: 'days',
      [LANGUAGES.MY]: 'hari'
    },
    hoursLeft: {
      [LANGUAGES.EN]: 'hours left',
      [LANGUAGES.MY]: 'jam lagi'
    },
    daysLeft: {
      [LANGUAGES.EN]: 'days left',
      [LANGUAGES.MY]: 'hari lagi'
    },
    // Messages
    requiresPass: {
      [LANGUAGES.EN]: 'This feature requires an Access Pass',
      [LANGUAGES.MY]: 'Ciri ini memerlukan Pas Akses'
    },
    limitReached: {
      [LANGUAGES.EN]: 'You\'ve used all your free estimates',
      [LANGUAGES.MY]: 'Anda telah menggunakan semua anggaran percuma'
    },
    usesOnly: {
      [LANGUAGES.EN]: 'uses only',
      [LANGUAGES.MY]: 'guna sahaja'
    },
    unlimited: {
      [LANGUAGES.EN]: 'Unlimited',
      [LANGUAGES.MY]: 'Tanpa Had'
    }
  }
};

/**
 * Get the translation of a key based on the current language
 * @param {String} key - Dot notation path to the translation string
 * @param {String} language - Current language code
 * @param {Object} replacements - Optional replacements for placeholders in the string
 * @returns {String} - Translated string
 */
export const getTranslation = (key, language = DEFAULT_LANGUAGE, replacements = {}) => {
  const keys = key.split('.');
  let value = translations;
  
  // Navigate through the nested object
  for (const k of keys) {
    if (value && value[k]) {
      value = value[k];
    } else {
      console.warn(`Translation missing for key: ${key}`);
      return key; // Return the key itself if translation not found
    }
  }
  
  // Get the translation for the specific language
  const translatedText = value[language] || value[DEFAULT_LANGUAGE] || key;
  
  // Replace placeholders if any
  if (replacements && Object.keys(replacements).length > 0) {
    return Object.keys(replacements).reduce((text, placeholder) => {
      const regex = new RegExp(`{${placeholder}}`, 'g');
      return text.replace(regex, replacements[placeholder]);
    }, translatedText);
  }
  
  return translatedText;
}; 